import {
	BBox,
	bbox,
	bboxPolygon,
	circle,
	difference,
	Feature,
	mask,
	MultiPolygon,
	point,
	Polygon,
	Position,
	Properties
} from '@turf/turf';
import mapboxgl, {
	LngLatBounds,
	LngLatBoundsLike,
	LngLatLike,
	Marker
} from 'mapbox-gl';

import 'mapbox-gl/dist/mapbox-gl.css';

let map: mapboxgl.Map | undefined;

let markers: Marker[] = [];

function polyMask(
	mask: Feature<Polygon, Properties>,
	bounds?: LngLatBounds
): Feature<Polygon | MultiPolygon> {
	const bboxPoly = bboxPolygon(bounds?.toArray().flat() as BBox);

	return difference(bboxPoly, mask) as Feature<Polygon, MultiPolygon>;
}

function Map(container?: HTMLElement, accessToken?: string): mapboxgl.Map {
	if (map) {
		return map;
	}

	mapboxgl.accessToken = accessToken;

	map = new mapboxgl.Map({
		container: container as HTMLElement,
		style: 'mapbox://styles/mapbox/dark-v10',
		center: {
			lng: -98.35,
			lat: 39.5
		},
		zoom: 3
	});

	return map;
}

Map.location = undefined as LngLatLike | undefined;

Map.zoom = 12;

Map.radius = 40;

Map.get = () => map;

Map.currentMask = undefined as Feature<Polygon | MultiPolygon> | undefined;
Map.outerFilterMask = undefined as Feature<Polygon | MultiPolygon> | undefined;
Map.innerFilterMask = undefined as Feature<Polygon | MultiPolygon> | undefined;
Map.test = undefined as Feature<Polygon | MultiPolygon> | undefined;

Map.outerFilterRadius = undefined as number | undefined;
Map.innerFilterRadius = undefined as number | undefined;

Map.setRadius = (radius: number) => {
	Map.radius = Number(radius);
};
Map.setOuterFilterRadius = (outerFilterRadius: number) => {
	Map.outerFilterRadius = Number(outerFilterRadius);
};
Map.setInnerFilterRadius = (innerFilterRadius: number) => {
	Map.innerFilterRadius = Number(innerFilterRadius);
};

const drawLayer = () => {
	Map.addCircleLayer(Map.radius);
	const center = point(Map.location as Position);

	const baseRadius = Map.radius;

	const circleGeo = circle(center, Map.radius ? Map.radius : baseRadius, {
		units: 'miles'
	});

	Map.currentMask = polyMask(circleGeo, map?.getBounds());

	if (map?.getLayer('circle')) {
		map?.removeLayer('circle');
	}

	if (map?.getSource('mask')) {
		map?.removeSource('mask');
	}

	map?.addSource('mask', {
		type: 'geojson',
		data: Map.currentMask
	});

	map?.addLayer({
		id: 'circle',
		type: 'fill',
		source: 'mask',
		paint: {
			'fill-color': 'red',
			'fill-opacity': 0.05
		},
		layout: {}
	});
};

Map.addCircleLayer = newRadius => {
	if (newRadius) {
		Map.setRadius(newRadius);
	}

	map?.on('load', () => {
		drawLayer();

		map?.on('zoom', drawLayer);
		map?.on('move', drawLayer);
	});
};

Map.addInnerLayer = () => {
	map?.on('load', () => {
		drawInnerLayer();

		map?.on('zoom', drawInnerLayer);
		map?.on('move', drawInnerLayer);
	});
};

const drawInnerLayer = () => {
	Map.addInnerLayer();

	const blueMarkerLocation = point([
		markers[0].getLngLat().lng,
		markers[0].getLngLat().lat
	] as Position);

	const circleGeo = circle(blueMarkerLocation, Map.outerFilterRadius, {
		units: 'miles'
	});

	const circleGeoInnerFilter = circle(
		blueMarkerLocation,
		Map.innerFilterRadius,
		{
			units: 'miles'
		}
	);

	Map.innerFilterMask = polyMask(circleGeoInnerFilter, map?.getBounds());
	Map.outerFilterMask = polyMask(circleGeo, map?.getBounds());

	if (map?.getLayer('innerCircle')) {
		map?.removeLayer('innerCircle');
	}

	if (map?.getLayer('innerFilterCircle')) {
		map?.removeLayer('innerFilterCircle');
	}

	if (map?.getSource('outerFilterMask')) {
		map?.removeSource('outerFilterMask');
	}

	map?.addSource('outerFilterMask', {
		type: 'geojson',
		data: Map.outerFilterMask
	});

	if (map?.getSource('innerFilterMask')) {
		map?.removeSource('innerFilterMask');
	}

	map?.addSource('innerFilterMask', {
		type: 'geojson',
		data: Map.innerFilterMask
	});

	map?.addLayer({
		id: 'innerFilterCircle',
		type: 'line',
		source: 'innerFilterMask',
		paint: {
			'line-color': '#2762E9',
			'line-opacity': 0.75
		},
		layout: {}
	});

	map?.addLayer({
		id: 'innerCircle',
		type: 'line',
		source: 'outerFilterMask',
		paint: {
			'line-color': '#2762E9',
			'line-opacity': 0.75
		},
		layout: {}
	});

	if (Map.innerFilterMask && Map.outerFilterMask) {
		const innerOuterRadiusFilterData = difference(
			Map.innerFilterMask,
			Map.outerFilterMask
		) as Feature<Polygon, MultiPolygon>;

		if (map?.getLayer('donut')) {
			map?.removeLayer('donut');
		}
		if (map?.getSource('donutmask')) {
			map?.removeSource('donutmask');
		}
		map?.addSource('donutmask', {
			type: 'geojson',
			data: innerOuterRadiusFilterData
		});
		map?.addLayer({
			id: 'donut',
			type: 'fill',
			source: 'donutmask',
			paint: {
				'fill-color': '#74F59E',
				'fill-opacity': 0.1
			},
			layout: {}
		});
	}
};

Map.addSearchRadius = (maxDistance, minDistance) => {
	Map.setOuterFilterRadius(maxDistance);
	Map.setInnerFilterRadius(minDistance);
};

Map.addMarker = (marker: Marker) => {
	if (map) {
		markers.push(marker);

		marker.addTo(Map());
	}
};

Map.getMarkers = (): Marker[] => {
	return markers;
};

Map.getLocationMarker = (): Marker | undefined =>
	markers.length > 0 ? markers[0] : undefined;

Map.clearMarkers = () => {
	markers.forEach(marker => marker.remove());

	markers = [];
};

Map.clearBusinessMarkers = () => {
	markers.splice(1).forEach(marker => marker.remove());
};

Map.remove = () => {
	(map as mapboxgl.Map).remove();

	map = undefined;
};

Map.fitBounds = () => {
	const markerCoordinates: Position[] = markers.map(marker => {
		const { lng, lat } = marker.getLngLat();

		return [lng, lat];
	});

	const bounds = bbox({ coordinates: markerCoordinates, type: 'MultiPoint' });

	map?.fitBounds(bounds as LngLatBoundsLike, { padding: 100 });
};

export default Map;
